import { Grid, MenuItem, TextField } from "@mui/material";
import React from "react";
import {
  DEXCO_CATEGORY_OPTIONS,
  DEXCO_DEANERY_OPTIONS,
  DEXCO_POST_TYPE_OPTIONS,
  buildDexcoPost,
  DexcoCategory,
  DexcoPostType,
} from "../../dexco/dexco_data";
import { toTitleCaseName } from "../../../utils/title_case";

export type DexcoFormValues = {
  category: DexcoCategory;
  rank: string;
  name: string;
  postType: DexcoPostType;
  postDetail: string;
  deaneryName: string;
  place: string;
  img: string;
};

export function getEmptyDexcoFormValues(): DexcoFormValues {
  return {
    category: "ICYM",
    rank: "",
    name: "",
    postType: "President",
    postDetail: "",
    deaneryName: "",
    place: "",
    img: "",
  };
}

export default function DexcoFormFields({
  formValues,
  setFormValues,
}: {
  formValues: DexcoFormValues;
  setFormValues: React.Dispatch<React.SetStateAction<DexcoFormValues>>;
}) {
  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          label="Section"
          value={formValues.category}
          onChange={(event) =>
            setFormValues((prev) => ({
              ...prev,
              category: event.target.value as DexcoCategory,
            }))
          }
        >
          {DEXCO_CATEGORY_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option} DEXCO
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          type="number"
          label="Rank"
          value={formValues.rank}
          onChange={(event) => setFormValues((prev) => ({ ...prev, rank: event.target.value }))}
          inputProps={{ min: 1, step: 1 }}
          helperText="Lower rank appears first. If you insert rank 2, the current 2 and below move down."
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Member Name"
          value={formValues.name}
          onChange={(event) =>
            setFormValues((prev) => ({ ...prev, name: toTitleCaseName(event.target.value) }))
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          label="Post"
          value={formValues.postType}
          onChange={(event) =>
            setFormValues((prev) => ({
              ...prev,
              postType: event.target.value as DexcoPostType,
              postDetail: event.target.value === "Representative" ? prev.postDetail : "",
              deaneryName: event.target.value === "Deanery President" ? prev.deaneryName : "",
            }))
          }
        >
          {DEXCO_POST_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        {formValues.postType === "Representative" ? (
          <TextField
            fullWidth
            label="Representative Designation"
            placeholder="Example: Regional Representative"
            value={formValues.postDetail}
            onChange={(event) =>
              setFormValues((prev) => ({ ...prev, postDetail: event.target.value }))
            }
            helperText="Use this for representative-style posts such as regional, national, formation, or custom labels."
          />
        ) : formValues.postType === "Deanery President" ? (
          <TextField
            select
            fullWidth
            label="Deanery"
            value={formValues.deaneryName}
            onChange={(event) =>
              setFormValues((prev) => ({ ...prev, deaneryName: event.target.value }))
            }
            helperText="The final post will be shown as President, Deanery Name."
          >
            {DEXCO_DEANERY_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            fullWidth
            label="Generated Post"
            value={buildDexcoPost(formValues.postType, formValues.postDetail, formValues.deaneryName)}
            InputProps={{ readOnly: true }}
            helperText="Standard post names are generated automatically."
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Place"
          value={formValues.place}
          onChange={(event) => setFormValues((prev) => ({ ...prev, place: event.target.value }))}
        />
      </Grid>
    </Grid>
  );
}
